import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AdminAuthProvider } from "./AuthContext";
import { ProtectedRoute } from "./ProtectedRoute";
import { AdminLayout } from "./AdminLayout";
import AdminLogin from "./pages/AdminLogin";
import Dashboard from "./pages/Dashboard";
import DataResource from "./pages/DataResource";
import Programs from "./pages/Programs";
import Content from "./pages/Content";
import Scaffold from "./pages/Scaffold";
import AdminStudents from "./pages/AdminStudents";
import AdminAnnouncements from "./pages/AdminAnnouncements";
import AdminSupport from "./pages/AdminSupport";
import AdminContent from "./pages/AdminContent";
import AdminChatbot from "./pages/AdminChatbot";
import AdminResults from "./pages/AdminResults";
import AdminCourses from "./pages/AdminCourses";
import { MODES } from "@/lib/api";
import { GALLERY_CATEGORIES, BLOG_CATEGORIES } from "@/lib/contentApi";

const GALLERY_FIELDS = [
  { k: "title", label: "Title" },
  { k: "category", label: "Category", type: "select", options: GALLERY_CATEGORIES },
  { k: "type", label: "Type", type: "select", options: ["photo", "video"] },
  { k: "url", label: "Media (image or video)", type: "file", accept: "image/*,video/*" },
  { k: "thumbnail", label: "Thumbnail (optional)", type: "file", accept: "image/*" },
  { k: "alt", label: "Alt text (accessibility)" },
];
const BLOG_FIELDS = [
  { k: "title", label: "Title" },
  { k: "slug", label: "Slug (optional, auto-generated)" },
  { k: "category", label: "Category", type: "select", options: BLOG_CATEGORIES },
  { k: "featuredImage", label: "Featured Image URL" },
  { k: "excerpt", label: "Excerpt", type: "textarea", rows: 2 },
  { k: "content", label: "Full Content", type: "textarea", rows: 8 },
  { k: "author", label: "Author" },
  { k: "seoTitle", label: "SEO Title" },
  { k: "seoDescription", label: "SEO Description", type: "textarea", rows: 2 },
  { k: "status", label: "Status", type: "select", options: ["draft", "published"] },
];
const TESTIMONIAL_FIELDS = [
  { k: "name", label: "Student Name" },
  { k: "course", label: "Course" },
  { k: "college", label: "College" },
  { k: "photo", label: "Student Photo URL" },
  { k: "text", label: "Testimonial", type: "textarea", rows: 4 },
  { k: "videoUrl", label: "Video Testimonial URL (optional)" },
  { k: "rating", label: "Rating (1-5)", type: "number" },
];
const EVENT_FIELDS = [
  { k: "title", label: "Title" },
  { k: "date", label: "Date" },
  { k: "location", label: "Location" },
  { k: "image", label: "Image URL" },
  { k: "description", label: "Description", type: "textarea", rows: 3 },
  { k: "link", label: "Registration Link" },
];

const Guarded = ({ children }) => (
  <ProtectedRoute>
    <AdminLayout>{children}</AdminLayout>
  </ProtectedRoute>
);

const DATE_FILTERS = [
  { key: "dateFrom", label: "From Date", type: "date" },
  { key: "dateTo", label: "To Date", type: "date" },
];

export default function AdminApp() {
  return (
    <AdminAuthProvider>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="login" element={<AdminLogin />} />
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Guarded><Dashboard /></Guarded>} />

        <Route path="enquiries" element={<Guarded>
          <DataResource
            resource="enquiries" title="Enquiries"
            columns={[
              { key: "fullName", label: "Name" },
              { key: "mobile", label: "Mobile" },
              { key: "email", label: "Email" },
              { key: "college", label: "College" },
              { key: "courseInterested", label: "Course" },
            ]}
            filters={[
              { key: "college", label: "College" },
              { key: "course", label: "Course" },
              { key: "passingYear", label: "Passing Year" },
              { key: "preferredMode", label: "Mode", type: "select", options: MODES },
              { key: "status", label: "Status", type: "select" },
              ...DATE_FILTERS,
            ]}
          />
        </Guarded>} />

        <Route path="applications" element={<Guarded>
          <DataResource
            resource="applications" title="Internship Applications" isApplications
            columns={[
              { key: "referenceNumber", label: "Ref #" },
              { key: "fullName", label: "Name" },
              { key: "mobile", label: "Mobile" },
              { key: "collegeName", label: "College" },
              { key: "programInterested", label: "Program" },
            ]}
            filters={[
              { key: "college", label: "College" },
              { key: "course", label: "Course" },
              { key: "passingYear", label: "Passing Year" },
              { key: "city", label: "City" },
              { key: "programInterested", label: "Program Interest" },
              { key: "preferredMode", label: "Mode", type: "select", options: MODES },
              { key: "status", label: "Status", type: "select" },
              ...DATE_FILTERS,
            ]}
          />
        </Guarded>} />

        <Route path="college-requests" element={<Guarded>
          <DataResource
            resource="college-requests" title="College Session Requests"
            columns={[
              { key: "collegeName", label: "College" },
              { key: "contactPerson", label: "Contact" },
              { key: "mobile", label: "Mobile" },
              { key: "email", label: "Email" },
              { key: "city", label: "City" },
            ]}
            filters={[
              { key: "city", label: "City" },
              { key: "status", label: "Status", type: "select" },
              ...DATE_FILTERS,
            ]}
          />
        </Guarded>} />

        <Route path="contact-messages" element={<Guarded>
          <DataResource
            resource="contact-messages" title="Contact Messages"
            columns={[
              { key: "name", label: "Name" },
              { key: "mobile", label: "Mobile" },
              { key: "email", label: "Email" },
              { key: "subject", label: "Subject" },
            ]}
            filters={[
              { key: "status", label: "Status", type: "select" },
              ...DATE_FILTERS,
            ]}
          />
        </Guarded>} />

        <Route path="course-recommendations" element={<Guarded>
          <DataResource
            resource="course-recommendations" title="Course Recommendations"
            columns={[
              { key: "fullName", label: "Name" },
              { key: "mobile", label: "Mobile" },
              { key: "email", label: "Email" },
              { key: "careerGoal", label: "Career Goal" },
              { key: "preferredDuration", label: "Duration" },
            ]}
            filters={[
              { key: "status", label: "Status", type: "select" },
              ...DATE_FILTERS,
            ]}
          />
        </Guarded>} />

        <Route path="career-applications" element={<Guarded>
          <DataResource
            resource="career-applications" title="Career Applications"
            columns={[
              { key: "referenceNumber", label: "Ref #" },
              { key: "fullName", label: "Name" },
              { key: "mobile", label: "Mobile" },
              { key: "email", label: "Email" },
              { key: "position", label: "Position" },
            ]}
            filters={[
              { key: "status", label: "Status", type: "select" },
              ...DATE_FILTERS,
            ]}
          />
        </Guarded>} />

        <Route path="programs" element={<Guarded><Programs /></Guarded>} />
        <Route path="courses" element={<Guarded><AdminCourses /></Guarded>} />
        <Route path="results" element={<Guarded><AdminResults /></Guarded>} />
        <Route path="content" element={<Guarded><Content /></Guarded>} />
        <Route path="students" element={<Guarded><AdminStudents /></Guarded>} />
        <Route path="announcements" element={<Guarded><AdminAnnouncements /></Guarded>} />
        <Route path="support" element={<Guarded><AdminSupport /></Guarded>} />
        <Route path="chatbot" element={<Guarded><AdminChatbot /></Guarded>} />

        <Route path="gallery" element={<Guarded><AdminContent kind="gallery" title="Gallery" note="Manage photos & videos — upload directly from your computer or paste a hosted URL." fields={GALLERY_FIELDS} toggle={{ field: "published", on: "Published", off: "Hidden" }} display={(it) => it.title || it.category || "Media"} /></Guarded>} />
        <Route path="blog" element={<Guarded><AdminContent kind="blog" title="Blog" note="Create and publish blog posts." fields={BLOG_FIELDS} toggle={{ field: "status", on: "Published", off: "Draft" }} display={(it) => it.title} /></Guarded>} />
        <Route path="testimonials" element={<Guarded><AdminContent kind="testimonials" title="Testimonials" note="Only approved testimonials appear on the public site. Do not publish fake testimonials." fields={TESTIMONIAL_FIELDS} toggle={{ field: "approved", on: "Approved", off: "Pending" }} display={(it) => it.name} /></Guarded>} />
        <Route path="events" element={<Guarded><AdminContent kind="events" title="Events" note="Manage events & sessions." fields={EVENT_FIELDS} toggle={{ field: "published", on: "Published", off: "Hidden" }} display={(it) => it.title} /></Guarded>} />

        <Route path="team" element={<Guarded><Scaffold testId="admin-team" title="Team" note="Manage team members and photos." /></Guarded>} />
        <Route path="payments" element={<Guarded>
          <DataResource
            resource="payments" title="Payments" isApplications={false}
            columns={[
              { key: "paymentRef", label: "Payment Ref" },
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "purposeLabel", label: "Purpose" },
              { key: "amount", label: "Amount (paise)" },
            ]}
            filters={[
              { key: "status", label: "Status", type: "select" },
              ...DATE_FILTERS,
            ]}
          />
        </Guarded>} />

        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>
    </AdminAuthProvider>
  );
}
